import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/formatters';
import { MatchContextModal } from '../components/shared/MatchContextModal';
import './HomePage.css';

const LEAGUE_BAR_COLOR = {
    EPL: '#CF142B', La_liga: '#c8a900', Serie_A: '#009246',
    Bundesliga: '#DD0000', Ligue_1: '#002395',
};
const DOT_COLORS     = { H: '#60a5fa', D: '#9ca3af', A: '#f87171' };
const SURFACE_COLORS = { Hard: '#3b82f6', Clay: '#f97316', Grass: '#22c55e' };
const LEVEL_COLORS   = { G: '#7c3aed', M: '#1d4ed8', F: '#0891b2', A: '#374151' };
const PAGE_SIZE = 50;

function getWeekLabel(dateStr) {
    const date  = new Date(dateStr);
    const day   = date.getDay();
    const diff  = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(date.setDate(diff));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = (d) => d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    return `${fmt(monday)} – ${fmt(sunday)}`;
}

function groupByWeek(picks) {
    const groups = {};
    picks.forEach(pick => {
        const label = getWeekLabel(pick.date);
        if (!groups[label]) groups[label] = [];
        groups[label].push(pick);
    });
    return groups;
}

export function HomePage({
    topPicks,
    accuracyByLeague,
    availableSeasons = [],
    onRefresh,
    onSeasonChange,
    fetchTopPicks,
    fetchMatchContext,
    isTennis = false,
    isBaseball = false,
    tennisPicks = [],
    tennisIsFallback = false,
    tennisStats = null,
    tennisLoading = false,
    tennisError = null,
    onTennisRefresh,
    baseballStats = null,
    baseballPicks = [],
    baseballLoading = false,
    onBaseballRefresh,
}) {
    const { t } = useTranslation();
    const [season, setSeason]           = useState('all');
    const [page, setPage]               = useState(0);
    const [totalCount, setTotalCount]   = useState(0);
    const [loading, setLoading]         = useState(false);
    const [picks, setPicks]             = useState([]);
    const [selectedMatch, setSelectedMatch] = useState(null);
    const navigate = useNavigate();

    const loadPicks = useCallback(async (p = 0) => {
        setLoading(true);
        const result = await fetchTopPicks(p * PAGE_SIZE);
        setPicks(result.picks || []);
        setTotalCount(result.totalCount || 0);
        setLoading(false);
    }, [fetchTopPicks]);

    useEffect(() => { loadPicks(0); }, []);

    function handleSeason(val) {
        setSeason(val);
        onSeasonChange(val);
    }

    function handleLeagueCardClick(leagueId) {
        const params = new URLSearchParams();
        params.set('league', leagueId);
        if (season && season !== 'all') params.set('season', season);
        navigate(`/analisis?${params.toString()}`);
    }

    function handlePrev() { const p = page - 1; setPage(p); loadPicks(p); }
    function handleNext() { const p = page + 1; setPage(p); loadPicks(p); }

    const weekGroups     = groupByWeek(picks);
    const totalPages     = Math.ceil(totalCount / PAGE_SIZE);
    const hasPrev        = page > 0;
    const hasNext        = (page + 1) * PAGE_SIZE < totalCount;
    const picksCount     = isTennis ? tennisPicks.length : isBaseball ? baseballPicks.length : totalCount;
    const isPicksLoading = isTennis ? tennisLoading : isBaseball ? baseballLoading : loading;
    const onRefreshPicks = isTennis ? onTennisRefresh : isBaseball ? onBaseballRefresh : () => loadPicks(page);

    return (
        <div className="home-section">

            {/* ── ACCURACY ── */}
            <div className="section-header">
                <h2>{t('home.accuracy_title')}</h2>
                <div className="season-selector">
                    <button
                        className={`season-pill${season === 'all' ? ' active' : ''}`}
                        onClick={() => handleSeason('all')}
                    >
                        {t('common.all_seasons')}
                    </button>
                    {availableSeasons.map(s => (
                        <button
                            key={s}
                            className={`season-pill${season === s ? ' active' : ''}`}
                            onClick={() => handleSeason(s)}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            <div className="league-accuracy-grid">
                {isTennis ? (
                    (tennisStats?.by_surface || []).map(s => (
                        <div key={s.surface} className="league-accuracy-card" style={{ cursor: 'default' }}>
                            <div className="card-inner">
                                <div className="league-name">{t(`tennis.surface_${s.surface.toLowerCase()}`)}</div>
                                <div className="league-acc">{(s.accuracy * 100).toFixed(1)}%</div>
                                <div className="league-bar">
                                    <div className="league-bar-fill" style={{ width: `${s.accuracy * 100}%`, background: SURFACE_COLORS[s.surface] || '#6b7280' }} />
                                </div>
                                <div className="league-total">{t('common.correct_of', { correct: s.correct, total: s.total })}</div>
                            </div>
                        </div>
                    ))
                ) : isBaseball ? (
                    baseballStats?.accuracy != null && (
                        <div className="league-accuracy-card league-accuracy-card--baseball" style={{ cursor: 'default' }}>
                            <div className="card-inner">
                                <div className="league-name">MLB</div>
                                <div className="league-acc" style={{ color: '#60a5fa' }}>{(baseballStats.accuracy.rate * 100).toFixed(1)}%</div>
                                <div className="league-bar">
                                    <div className="league-bar-fill" style={{ width: `${baseballStats.accuracy.rate * 100}%`, background: '#3b82f6' }} />
                                </div>
                                <div className="league-total">{t('common.correct_of', { correct: baseballStats.accuracy.correct, total: baseballStats.accuracy.total })}</div>
                            </div>
                        </div>
                    )
                ) : (
                    <>
                        {accuracyByLeague.length === 0 ? (
                            <div className="empty-state">
                                <p>{t('home.no_predictions', {
                                    period: season === 'all' ? t('home.all_seasons_label') : t('home.season_label', { season })
                                })}</p>
                            </div>
                        ) : accuracyByLeague.map(lg => (
                            <div
                                key={lg.league}
                                className={`league-accuracy-card flag-${lg.league}`}
                                onClick={() => handleLeagueCardClick(lg.league)}
                            >
                                <div className="flag-bg" />
                                <div className="card-inner">
                                    <div className="league-name">{t(`leagues.${lg.league}`)}</div>
                                    <div className="league-acc">{(lg.accuracy * 100).toFixed(1)}%</div>
                                    <div className="league-bar">
                                        <div className="league-bar-fill" style={{ width: `${lg.accuracy * 100}%`, background: LEAGUE_BAR_COLOR[lg.league] || '#f97316' }} />
                                    </div>
                                    <div className="league-total">{t('common.correct_of', { correct: lg.correct, total: lg.total })}</div>
                                </div>
                            </div>
                        ))}

                        {tennisStats?.accuracy != null && (() => {
                            const tCorrect = tennisStats.by_surface?.reduce((s, x) => s + x.correct, 0) ?? null;
                            const tTotal   = tennisStats.by_surface?.reduce((s, x) => s + x.total,   0) ?? null;
                            return (
                                <div className="league-accuracy-card league-accuracy-card--tennis" onClick={() => handleLeagueCardClick('Tennis')}>
                                    <div className="card-inner">
                                        <div className="league-name">{t('leagues.Tennis')}</div>
                                        <div className="league-acc" style={{ color: '#f59e0b' }}>{(tennisStats.accuracy * 100).toFixed(1)}%</div>
                                        <div className="league-bar">
                                            <div className="league-bar-fill" style={{ width: `${tennisStats.accuracy * 100}%`, background: '#f59e0b' }} />
                                        </div>
                                        <div className="league-total">{tCorrect != null ? t('common.correct_of', { correct: tCorrect, total: tTotal }) : ''}</div>
                                    </div>
                                </div>
                            );
                        })()}

                        {baseballStats?.accuracy != null && (
                            <div className="league-accuracy-card league-accuracy-card--baseball" onClick={() => handleLeagueCardClick('Baseball')}>
                                <div className="card-inner">
                                    <div className="league-name">MLB</div>
                                    <div className="league-acc" style={{ color: '#60a5fa' }}>{(baseballStats.accuracy.rate * 100).toFixed(1)}%</div>
                                    <div className="league-bar">
                                        <div className="league-bar-fill" style={{ width: `${baseballStats.accuracy.rate * 100}%`, background: '#3b82f6' }} />
                                    </div>
                                    <div className="league-total">{t('common.correct_of', { correct: baseballStats.accuracy.correct, total: baseballStats.accuracy.total })}</div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ── TOP PICKS ── */}
            <div className="section-header" style={{ marginTop: '32px' }}>
                <h2>
                    {t('home.top_picks_title')}
                    {picksCount > 0 && <span className="picks-total-badge">{picksCount}</span>}
                </h2>
                <button onClick={onRefreshPicks} className="refresh-button">{t('common.refresh')}</button>
            </div>

            {isPicksLoading ? (
                <div className="empty-state"><p>{t('common.loading')}</p></div>

            ) : isTennis ? (
                tennisError ? (
                    <div className="empty-state"><p>{t('tennis.error')} (HTTP {tennisError})</p></div>
                ) : tennisPicks.length === 0 ? (
                    <div className="empty-state">
                        <p>{t('tennis.no_picks')}</p>
                        <p className="subtitle">{t('tennis.no_picks_subtitle')}</p>
                    </div>
                ) : (
                    <>
                    {tennisIsFallback && (
                        <div className="picks-fallback-banner">
                            {t('tennis.fallback_banner')}
                        </div>
                    )}
                    <div className="top-picks-grid">
                        {tennisPicks.map(pick => {
                            const conf     = Math.round(pick.confidence * 100);
                            const probWin  = Math.round(pick.prob_winner * 100);
                            const probLose = Math.round(pick.prob_underdog * 100);
                            return (
                                <div key={pick.match_id} className="top-pick-card top-pick-card--tennis">
                                    <div className="pick-header">
                                        <div className="pick-header-left">
                                            <span className="league-tag" style={{ background: LEVEL_COLORS[pick.level] || '#374151' }}>{pick.level_name}</span>
                                            <span className="pick-date">{pick.tournament}</span>
                                        </div>
                                        <div className="pick-header-right">
                                            <span className="pick-conf-big">{conf}%</span>
                                            <span className="pick-conf-label">{t('common.confidence')}</span>
                                        </div>
                                    </div>
                                    <div className="pick-body">
                                        <div className="pick-teams">
                                            <div className={`pick-team-name home${pick.predicted_winner === pick.p1_name ? ' pick-team--predicted' : ''}`}>{pick.p1_name}</div>
                                            <div className="pick-separator">{t('common.vs')}</div>
                                            <div className={`pick-team-name away${pick.predicted_winner === pick.p2_name ? ' pick-team--predicted' : ''}`}>{pick.p2_name}</div>
                                        </div>
                                        <div className="pick-bar">
                                            <div className="pick-bar-seg home" style={{ width: `${pick.predicted_winner === pick.p1_name ? probWin : probLose}%` }} />
                                            <div className="pick-bar-seg away" style={{ width: `${pick.predicted_winner === pick.p2_name ? probWin : probLose}%` }} />
                                        </div>
                                        <div className="pick-seg-labels" style={{ justifyContent: 'space-between' }}>
                                            <div className="pick-seg-label">
                                                <span className="seg-val">{pick.predicted_winner === pick.p1_name ? probWin : probLose}%</span>
                                            </div>
                                            <span className="pick-surface-badge" style={{ background: SURFACE_COLORS[pick.surface] || '#6b7280' }}>{pick.surface}</span>
                                            <div className="pick-seg-label right">
                                                <span className="seg-val">{pick.predicted_winner === pick.p2_name ? probWin : probLose}%</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="pick-footer">
                                        <span className="pick-pred-label">
                                            {new Date(pick.date).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}
                                            {pick.round && ` · ${pick.round}`}
                                        </span>
                                        <span className="pick-pred-team">{pick.predicted_winner}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    </>
                )

            ) : isBaseball ? (
                baseballPicks.length === 0 ? (
                    <div className="empty-state">
                        <p>No hay próximos partidos con predicción disponible.</p>
                        <p className="subtitle">Vuelve a intentarlo más tarde.</p>
                    </div>
                ) : (
                    <div className="top-picks-grid">
                        {baseballPicks.map(game => {
                            const probH = Math.round((game.prob_home || 0) * 100);
                            const probA = Math.round((game.prob_away || 0) * 100);
                            const conf  = Math.round((game.confidence || 0) * 100);
                            const isHome = game.predicted_winner === 'home';
                            const winnerName = isHome ? game.home_team : game.away_team;
                            return (
                                <div key={game.id} className="top-pick-card top-pick-card--tennis">
                                    <div className="pick-header">
                                        <div className="pick-header-left">
                                            <span className="league-tag" style={{ background: '#1565c0' }}>MLB</span>
                                            <span className="pick-date">{new Date(game.game_date).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}</span>
                                        </div>
                                        <div className="pick-header-right">
                                            <span className="pick-conf-big">{conf}%</span>
                                            <span className="pick-conf-label">{t('common.confidence')}</span>
                                        </div>
                                    </div>
                                    <div className="pick-body">
                                        <div className="pick-teams">
                                            <div className={`pick-team-name home${isHome ? ' pick-team--predicted' : ''}`}>{game.home_team}</div>
                                            <div className="pick-separator">{t('common.vs')}</div>
                                            <div className={`pick-team-name away${!isHome ? ' pick-team--predicted' : ''}`}>{game.away_team}</div>
                                        </div>
                                        <div className="pick-bar">
                                            <div className="pick-bar-seg home" style={{ width: `${probH}%` }} />
                                            <div className="pick-bar-seg away" style={{ width: `${probA}%` }} />
                                        </div>
                                        <div className="pick-seg-labels" style={{ justifyContent: 'space-between' }}>
                                            <div className="pick-seg-label"><span className="seg-val">{probH}%</span></div>
                                            <span className="pick-surface-badge" style={{ background: '#1565c0' }}>{t('results.H_short', 'Local')}</span>
                                            <div className="pick-seg-label right"><span className="seg-val">{probA}%</span></div>
                                        </div>
                                    </div>
                                    <div className="pick-footer">
                                        <span className="pick-pred-label">
                                            {game.home_starter ? `P: ${game.home_starter}` : 'Local'}
                                            {game.away_starter ? ` vs ${game.away_starter}` : ''}
                                        </span>
                                        <span className="pick-pred-team">{winnerName}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )

            ) : picks.length === 0 ? (
                <div className="empty-state">
                    <p>{t('home.no_picks')}</p>
                    <p className="subtitle">{t('home.no_picks_subtitle')}</p>
                </div>
            ) : (
                <>
                    {Object.entries(weekGroups).map(([weekLabel, weekPicks]) => (
                        <div key={weekLabel} className="week-group">
                            <div className="week-label">
                                <div className="week-label-left">
                                    <span className="week-label-tag">{t('upcoming.matchday')}</span>
                                    <span className="week-label-date">{weekLabel}</span>
                                </div>
                                <div className="week-label-right">
                                    <span className="week-count-num">{weekPicks.length}</span>
                                    <span className="week-count-label">picks</span>
                                </div>
                            </div>
                            <div className="top-picks-grid">
                                {weekPicks.map(match => {
                                    const probH = Math.round((match.probabilities?.home ?? 0) * 100);
                                    const probD = Math.round((match.probabilities?.draw ?? 0) * 100);
                                    const probA = Math.round((match.probabilities?.away ?? 0) * 100);
                                    const pred  = match.predicted_result;
                                    const conf  = Math.round((match.confidence ?? 0) * 100);
                                    const winnerTeam = pred === 'H' ? match.home_team : pred === 'A' ? match.away_team : null;
                                    return (
                                        <div
                                            key={match.match_id}
                                            className="top-pick-card"
                                            onClick={() => setSelectedMatch(match)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="pick-header">
                                                <div className="pick-header-left">
                                                    <span className={`league-tag league-tag--${match.league}`}>{t(`leagues.${match.league}`)}</span>
                                                    <span className="pick-date">{formatDate(match.date)}</span>
                                                </div>
                                                <div className="pick-header-right">
                                                    <span className="pick-conf-big">{conf}%</span>
                                                    <span className="pick-conf-label">{t('common.confidence')}</span>
                                                </div>
                                            </div>
                                            <div className="pick-body">
                                                <div className="pick-teams">
                                                    <div className="pick-team-name home">{match.home_team}</div>
                                                    <div className="pick-separator">{t('common.vs')}</div>
                                                    <div className="pick-team-name away">{match.away_team}</div>
                                                </div>
                                                <div className="pick-bar">
                                                    <div className="pick-bar-seg home" style={{ width: `${probH}%` }} />
                                                    <div className="pick-bar-seg draw" style={{ width: `${probD}%` }} />
                                                    <div className="pick-bar-seg away" style={{ width: `${probA}%` }} />
                                                </div>
                                                <div className="pick-seg-labels">
                                                    <div className="pick-seg-label">
                                                        <span className="seg-letter seg-letter--home">{t('results.H_short')}</span>
                                                        <span className="seg-val">{probH}%</span>
                                                    </div>
                                                    <div className="pick-seg-label center">
                                                        <span className="seg-letter seg-letter--draw">{t('results.D_short')}</span>
                                                        <span className="seg-val">{probD}%</span>
                                                    </div>
                                                    <div className="pick-seg-label right">
                                                        <span className="seg-letter seg-letter--away">{t('results.A_short')}</span>
                                                        <span className="seg-val">{probA}%</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="pick-footer">
                                                <div className="pick-pred-type">
                                                    <span className="pick-dot" style={{ background: DOT_COLORS[pred] || '#9ca3af' }} />
                                                    <span className="pick-pred-label">{t(`results.${pred}`)}</span>
                                                </div>
                                                <span className="pick-pred-team">{winnerTeam || t('results.D_short')}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    {totalPages > 1 && (
                        <div className="picks-pagination">
                            <button className="page-btn" onClick={handlePrev} disabled={!hasPrev}>
                                ← {t('historial.prev').replace('←', '').trim()}
                            </button>
                            <span className="page-info">
                                {page + 1} / {totalPages}
                                <span className="page-total"> · {totalCount} picks</span>
                            </span>
                            <button className="page-btn" onClick={handleNext} disabled={!hasNext}>
                                {t('historial.next').replace('→', '').trim()} →
                            </button>
                        </div>
                    )}
                </>
            )}

            {selectedMatch && (
                <MatchContextModal
                    match={selectedMatch}
                    onClose={() => setSelectedMatch(null)}
                    fetchMatchContext={fetchMatchContext}
                />
            )}

        </div>
    );
}
